import { useEffect, useState } from 'react';
import type { InputEvent } from '../../engine/types';
import { inputManager } from '../../input/manager';
import { noteName, octaveOf } from '../../music/pitchNames';
import { useSettings } from '../hooks/stores';
import { INPUT_LABELS } from '../inputControl';

interface Shown {
  id: number;
  event: Extract<InputEvent, { type: 'noteOn' | 'onset' }>;
}

let counter = 0;

/** Affiche en direct les notes reconnues par la source active, pour vérifier son piano et son micro. */
export function InputCheck() {
  const settings = useSettings();
  const [events, setEvents] = useState<Shown[]>([]);

  useEffect(
    () =>
      inputManager.subscribe((e) => {
        if (e.type === 'noteOn' || e.type === 'onset') {
          setEvents((list) => [...list.slice(-7), { id: ++counter, event: e }]);
        }
      }),
    [],
  );

  const last = events[events.length - 1];
  const describe = (e: Shown['event']): string => {
    if (e.type === 'onset') return 'attaque sans hauteur reconnue';
    const octave = settings.naming === 'fr' ? octaveOf(e.pitch) - 1 : octaveOf(e.pitch);
    const cents = e.cents !== undefined ? ` (${e.cents >= 0 ? '+' : ''}${Math.round(e.cents)} cents)` : '';
    return `${noteName(e.pitch, settings.naming)}${octave}${cents}`;
  };

  return (
    <div className="input-check">
      <div className={`big-note ${last?.event.type === 'noteOn' && last.event.confidence === 'sure' ? 'sure' : 'unsure'}`} aria-live="polite">
        {last ? (last.event.type === 'noteOn' ? describe(last.event) : '?') : '—'}
      </div>
      <ol className="event-list" aria-label="Dernières détections">
        {events
          .slice()
          .reverse()
          .map(({ id, event }) => (
            <li key={id} className={event.type === 'noteOn' && event.confidence === 'sure' ? 'sure' : 'unsure'}>
              <span>{describe(event)}</span>
              <span className="muted">
                {event.type === 'noteOn' ? (event.confidence === 'sure' ? 'sûre' : 'incertaine') : 'incertaine'} · {INPUT_LABELS[event.source]}
              </span>
            </li>
          ))}
      </ol>
      <p className="muted">Jouez quelques notes isolées : la note reconnue et sa confiance s’affichent ici. Une détection incertaine n’est jamais comptée comme juste pendant un exercice.</p>
    </div>
  );
}
